export type FeedbackSound =
  | "slide"
  | "match"
  | "drop"
  | "attack"
  | "hit"
  | "skill"
  | "reward"
  | "combo";

interface PlayOptions {
  delay?: number;
  rate?: number;
  tier?: number;
  volume?: number;
}

const SFX_PATHS: Record<Exclude<FeedbackSound, "combo"> | "chain", string> = {
  slide: "/assets/audio/sfx/slide.ogg",
  match: "/assets/audio/sfx/match.ogg",
  chain: "/assets/audio/sfx/chain.ogg",
  drop: "/assets/audio/sfx/drop.ogg",
  attack: "/assets/audio/sfx/attack.ogg",
  hit: "/assets/audio/sfx/hit.ogg",
  skill: "/assets/audio/sfx/skill.ogg",
  reward: "/assets/audio/sfx/reward.ogg",
};
const CHAIN_RATES = [1, 2 ** (4 / 12), 2 ** (7 / 12), 2, 2 ** (16 / 12)];

export class AudioFeedback {
  private context: AudioContext | null = null;
  private unlocked = false;
  private loading: Promise<void> | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private muted = false;

  async unlock(): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
    this.unlocked = this.context.state === "running";
    if (!this.unlocked) return;
    this.loading ??= this.loadBuffers();
    try {
      await this.loading;
    } catch (error) {
      this.loading = null;
      console.warn("Breacher SFX preload failed", error);
      return;
    }
  }

  play(sound: FeedbackSound, options: PlayOptions = {}): void {
    if (this.muted || !this.unlocked || !this.context) return;
    const key = sound === "combo" ? "chain" : sound;
    const buffer = this.buffers.get(key);
    if (!buffer) return;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    if (sound === "combo") {
      const tier = Math.max(1, Math.min(5, Math.round(options.tier ?? 1)));
      source.playbackRate.value = CHAIN_RATES[tier - 1];
    } else if (options.rate) {
      source.playbackRate.value = Math.max(0.5, Math.min(1.75, options.rate));
    }
    gain.gain.value = options.volume ?? (sound === "hit" ? 0.34 : sound === "combo" ? 0.28 : 0.24);
    source.connect(gain).connect(this.context.destination);
    source.start(this.context.currentTime + Math.max(0, options.delay ?? 0));
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  dispose(): void {
    void this.context?.close();
    this.context = null;
    this.unlocked = false;
    this.buffers.clear();
  }

  private async loadBuffers(): Promise<void> {
    if (!this.context) return;
    await Promise.all(
      Object.entries(SFX_PATHS).map(async ([key, path]) => {
        const response = await fetch(path);
        if (!response.ok) throw new Error(`audio ${key} returned ${response.status}`);
        const buffer = await this.context!.decodeAudioData(await response.arrayBuffer());
        this.buffers.set(key, buffer);
      }),
    );
  }
}
